import React, { useEffect, useRef } from 'react';
import { roadMapBridge } from '../../render/roadMapBridge';
import { ROAD, WALK, BLOCK } from '../../render/roadMap';
import { camBasis } from '../../services/worldObjects';
import { useWorldStore } from '../../state/worldStore';
import { TOWN_LOCATIONS } from '../../rooms';

// ── THE RADAR ────────────────────────────────────────────────────────────────
//
// The open-world staple: a round map in the corner, centred on you, turning as
// you turn, so "up" is always where you are facing. Roads come from the road
// map the street lights already read off the model, venues are blips in their
// own accent colours, and a venue off the edge pins to the rim in its direction
// — which makes it a compass to everything at once.
//
// Canvas 2D, redrawn ~20 times a second with no React renders: the map is one
// pre-rendered image, rotated and blitted, plus a dozen dots.

const SIZE = 176;               // CSS pixels
const RANGE = 46;               // world units from centre to rim
const PPU = SIZE / 2 / RANGE;   // pixels per world unit

function paintMap(map) {
  const c = document.createElement('canvas');
  c.width = map.w; c.height = map.h;
  const g = c.getContext('2d');
  const img = g.createImageData(map.w, map.h);
  const col = {
    [ROAD]: [168, 176, 186, 255],
    [WALK]: [64, 70, 80, 255],
    [BLOCK]: [34, 38, 46, 255],
    0: [22, 26, 32, 255],
  };
  for (let r = 0; r < map.h; r++) {
    for (let x = 0; x < map.w; x++) {
      const k = col[map.cls[r * map.w + x]] || col[0];
      const o = (r * map.w + x) * 4;     // row r = z increasing → image row downward
      img.data[o] = k[0]; img.data[o + 1] = k[1]; img.data[o + 2] = k[2]; img.data[o + 3] = k[3];
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}

export default function Radar({ style }) {
  const ref = useRef(null);
  const mapImg = useRef(null);
  const mapInfo = useRef(null);

  useEffect(() => roadMapBridge.subscribe((map) => {
    mapImg.current = paintMap(map);
    mapInfo.current = { minX: map.rect.minX, minZ: map.rect.minZ, u: map.unitsPerTexel };
  }), []);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = SIZE * dpr; cv.height = SIZE * dpr;
    const g = cv.getContext('2d');
    let raf = 0; let last = 0;
    const draw = (t) => {
      raf = requestAnimationFrame(draw);
      if (t - last < 45) return;
      last = t;
      const R = SIZE / 2;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, SIZE, SIZE);
      g.save();
      g.beginPath(); g.arc(R, R, R - 3, 0, Math.PI * 2); g.clip();
      g.fillStyle = 'rgba(14,17,22,0.92)';
      g.fillRect(0, 0, SIZE, SIZE);

      const px = camBasis.px; const pz = camBasis.pz;
      // Forward in map space (x right, z down). Turn the map so it points up.
      const theta = -Math.PI / 2 - Math.atan2(camBasis.fz, camBasis.fx);
      const rot = (dx, dz) => {
        const c = Math.cos(theta); const s = Math.sin(theta);
        return [R + (dx * c - dz * s) * PPU, R + (dx * s + dz * c) * PPU];
      };

      if (mapImg.current && mapInfo.current) {
        const { minX, minZ, u } = mapInfo.current;
        g.save();
        g.translate(R, R);
        g.rotate(theta);
        g.scale(PPU * u, PPU * u);
        g.globalAlpha = 0.95;
        g.imageSmoothingEnabled = true;
        g.drawImage(mapImg.current, -(px - minX) / u, -(pz - minZ) / u);
        g.restore();
      }

      // Venue blips. Off the edge → pinned to the rim, smaller.
      const anchors = useWorldStore.getState().venueAnchors;
      if (anchors) {
        for (const loc of TOWN_LOCATIONS) {
          const a = anchors[loc.key];
          if (!a) continue;
          let [bx, by] = rot(a[0] - px, a[2] - pz);
          const dx = bx - R; const dy = by - R;
          const d = Math.hypot(dx, dy);
          const edge = R - 12;
          const pinned = d > edge;
          if (pinned) { bx = R + (dx / d) * edge; by = R + (dy / d) * edge; }
          g.beginPath();
          g.arc(bx, by, pinned ? 4.5 : 6, 0, Math.PI * 2);
          g.fillStyle = loc.accent;
          g.fill();
          g.lineWidth = 1.5; g.strokeStyle = 'rgba(0,0,0,0.85)'; g.stroke();
        }
      }

      // Vignette ring inside the rim.
      const vg = g.createRadialGradient(R, R, R * 0.62, R, R, R);
      vg.addColorStop(0, 'rgba(0,0,0,0)');
      vg.addColorStop(1, 'rgba(0,0,0,0.55)');
      g.fillStyle = vg; g.fillRect(0, 0, SIZE, SIZE);
      g.restore();

      // North: −z, which is "up" in map space before the turn.
      const [tx, ty] = rot(0, -1000);
      const tl = Math.hypot(tx - R, ty - R) || 1;
      const nx = R + ((tx - R) / tl) * (R - 10); const ny = R + ((ty - R) / tl) * (R - 10);
      g.beginPath(); g.arc(nx, ny, 8, 0, Math.PI * 2);
      g.fillStyle = 'rgba(10,12,16,0.95)'; g.fill();
      g.font = '800 10px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = '#ffffff'; g.fillText('N', nx, ny + 0.5);

      // You.
      g.save();
      g.translate(R, R);
      g.beginPath();
      g.moveTo(0, -9); g.lineTo(6.5, 7); g.lineTo(0, 3.5); g.lineTo(-6.5, 7); g.closePath();
      g.fillStyle = '#ffffff'; g.fill();
      g.lineWidth = 1.5; g.strokeStyle = 'rgba(0,0,0,0.9)'; g.stroke();
      g.restore();

      // Rim.
      g.beginPath(); g.arc(R, R, R - 2, 0, Math.PI * 2);
      g.lineWidth = 3; g.strokeStyle = 'rgba(8,10,14,0.9)'; g.stroke();
      g.beginPath(); g.arc(R, R, R - 4.5, 0, Math.PI * 2);
      g.lineWidth = 1; g.strokeStyle = 'rgba(255,210,125,0.35)'; g.stroke();
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={ref}
      aria-label="Radar"
      style={{ width: SIZE, height: SIZE, borderRadius: '50%', boxShadow: '0 6px 22px rgba(0,0,0,0.45)', ...style }}
    />
  );
}

export const RADAR_SIZE = SIZE;
